import { logPerformance } from './frontendLogger';

class PerformanceLogger {
  private observer: PerformanceObserver | null = null;

  constructor() {
    this.initializeObservers();
    this.logInitialMetrics();
  }

  private initializeObservers() {
    // Observe navigation timing
    if ('PerformanceObserver' in window) {
      try {
        // Navigation timing
        this.observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.entryType === 'navigation') {
              const navEntry = entry as PerformanceNavigationTiming;
              this.logNavigationMetrics(navEntry);
            }
          }
        });
        this.observer.observe({ entryTypes: ['navigation'] });

        // Long tasks (blocking main thread)
        const longTaskObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            logPerformance('long-task', entry.duration, {
              name: entry.name,
              startTime: entry.startTime,
            });
          }
        });
        
        // Only observe if supported
        if (PerformanceObserver.supportedEntryTypes.includes('longtask')) {
          longTaskObserver.observe({ entryTypes: ['longtask'] });
        }

        // Largest Contentful Paint
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          logPerformance('lcp', lastEntry.startTime, {
            element: (lastEntry as any).element?.tagName,
            size: (lastEntry as any).size,
          });
        });
        
        if (PerformanceObserver.supportedEntryTypes.includes('largest-contentful-paint')) {
          lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
        }

        // First Input Delay
        const fidObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            const fidEntry = entry as any;
            logPerformance('fid', fidEntry.processingStart - fidEntry.startTime, {
              name: entry.name,
            });
          }
        });
        
        if (PerformanceObserver.supportedEntryTypes.includes('first-input')) {
          fidObserver.observe({ entryTypes: ['first-input'] });
        }

        // Layout shifts
        const clsObserver = new PerformanceObserver((list) => {
          let clsScore = 0;
          for (const entry of list.getEntries()) {
            if (!(entry as any).hadRecentInput) {
              clsScore += (entry as any).value;
            }
          }
          if (clsScore > 0) {
            logPerformance('cls', clsScore);
          }
        });
        
        if (PerformanceObserver.supportedEntryTypes.includes('layout-shift')) {
          clsObserver.observe({ entryTypes: ['layout-shift'] });
        }

      } catch (error) {
        console.error('Failed to setup performance observers:', error);
      }
    }

    // Log memory usage periodically (if available)
    if ('memory' in performance && process.env.NODE_ENV !== 'production') {
      setInterval(() => {
        const memory = (performance as any).memory;
        logPerformance('memory', memory.usedJSHeapSize, {
          totalJSHeapSize: memory.totalJSHeapSize,
          jsHeapSizeLimit: memory.jsHeapSizeLimit,
        });
      }, 300000); // Every 5 minutes (reduced from 30 seconds)
    }
  }

  private logInitialMetrics() {
    // Wait for page load
    window.addEventListener('load', () => {
      setTimeout(() => {
        this.logNavigationMetrics();
        this.logResourceMetrics();
      }, 0);
    });
  }

  private logNavigationMetrics(entry?: PerformanceNavigationTiming) {
    // Skip performance logging in production
    if (process.env.NODE_ENV === 'production') return;
    
    const navEntry = entry || performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    
    if (!navEntry) return;

    // Core Web Vitals and other metrics
    const metrics = {
      // Time to First Byte
      ttfb: navEntry.responseStart - navEntry.requestStart,
      // DOM Content Loaded
      domContentLoaded: navEntry.domContentLoadedEventEnd - navEntry.domContentLoadedEventStart,
      // Load Complete
      loadComplete: navEntry.loadEventEnd - navEntry.loadEventStart,
      // Total Page Load Time
      totalLoadTime: navEntry.loadEventEnd - navEntry.fetchStart,
      // DNS Lookup
      dnsLookup: navEntry.domainLookupEnd - navEntry.domainLookupStart,
      // TCP Connection
      tcpConnection: navEntry.connectEnd - navEntry.connectStart,
      // Request Duration
      requestDuration: navEntry.responseEnd - navEntry.requestStart,
      // Response Duration
      responseDuration: navEntry.responseEnd - navEntry.responseStart,
      // DOM Processing
      domProcessing: navEntry.domComplete - navEntry.domInteractive,
    };

    Object.entries(metrics).forEach(([name, value]) => {
      if (value > 0) {
        logPerformance(`navigation.${name}`, value);
      }
    });
  }

  private logResourceMetrics() {
    // Skip resource logging in production
    if (process.env.NODE_ENV === 'production') return;
    
    const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    
    // Group resources by type
    const resourcesByType: Record<string, number[]> = {};
    
    resources.forEach((resource) => {
      const type = this.getResourceType(resource.name);
      if (!resourcesByType[type]) {
        resourcesByType[type] = [];
      }
      resourcesByType[type].push(resource.duration);
    });

    // Log aggregated metrics by type
    Object.entries(resourcesByType).forEach(([type, durations]) => {
      const avg = durations.reduce((a, b) => a + b, 0) / durations.length;
      const max = Math.max(...durations);
      
      logPerformance(`resources.${type}`, avg, {
        count: durations.length,
        avgDuration: avg,
        maxDuration: max,
        totalDuration: durations.reduce((a, b) => a + b, 0),
      });
    });
  }

  private getResourceType(url: string): string {
    if (url.match(/\.(js|mjs)$/i)) return 'script';
    if (url.match(/\.css$/i)) return 'style';
    if (url.match(/\.(jpg|jpeg|png|gif|svg|webp)$/i)) return 'image';
    if (url.match(/\.(woff|woff2|ttf|eot)$/i)) return 'font';
    if (url.includes('/api/')) return 'api';
    return 'other';
  }

  // Manual timing API
  private timings = new Map<string, number>();

  startTiming(label: string) {
    this.timings.set(label, performance.now());
  }

  endTiming(label: string, metadata?: any) {
    const startTime = this.timings.get(label);
    if (startTime) {
      const duration = performance.now() - startTime;
      this.timings.delete(label);
      logPerformance(`timing.${label}`, duration, metadata);
      return duration;
    }
    return 0;
  }

  // React component performance
  measureComponent(componentName: string, phase: 'mount' | 'update', actualDuration: number) {
    logPerformance(`component.${componentName}.${phase}`, actualDuration);
  }
}

// Create singleton instance
const performanceLogger = new PerformanceLogger();

export default performanceLogger;

// Export convenience methods
export const startTiming = performanceLogger.startTiming.bind(performanceLogger);
export const endTiming = performanceLogger.endTiming.bind(performanceLogger);
export const measureComponent = performanceLogger.measureComponent.bind(performanceLogger);